class LpFbBundleBuilder extends HTMLElement {
  connectedCallback() {
    this.threshold = parseInt(this.dataset.shippingThreshold, 10) || 8500;
    this.items = [];
    this.uidSeq = 0;

    this.tabButtons = Array.from(this.querySelectorAll('[data-bb-tab]'));
    this.itemsContainers = Array.from(this.querySelectorAll('[data-bb-items]'));
    this.checkoutButtons = Array.from(this.querySelectorAll('[data-bb-checkout]'));
    this.totalsBlocks = Array.from(this.querySelectorAll('[data-bb-totals]'));
    this.bar = this.querySelector('[data-bb-bar]');
    this.barToggle = this.querySelector('[data-bb-bar-toggle]');
    this.barPill = this.querySelector('[data-bb-bar-pill]');
    this.barBuild = this.querySelector('[data-bb-bar-build]');
    this.emptyEls = Array.from(this.querySelectorAll('[data-bb-empty]'));
    this.preorderNotes = Array.from(this.querySelectorAll('[data-bb-preorder-note]'));
    this.itemTemplate = this.querySelector('[data-bb-item-template]');

    this.cards = Array.from(this.querySelectorAll('[data-bb-card]'))
      .map((el) => this.initCard(el))
      .filter(Boolean);

    this.sg = this.querySelector('[data-bb-sg]');
    this.sgDialog = this.querySelector('[data-bb-sg-dialog]');
    this.sgOpenButton = this.querySelector('[data-bb-sg-open]');
    this.sgCloseButtons = Array.from(this.querySelectorAll('[data-bb-sg-close]'));
    this.sgTabButtons = Array.from(this.querySelectorAll('[data-bb-sg-tab]'));
    this.sgPanels = Array.from(this.querySelectorAll('[data-bb-sg-panel]'));

    this.tabButtons.forEach((button) => button.addEventListener('click', this.handleTab));
    this.checkoutButtons.forEach((button) => button.addEventListener('click', this.handleCheckout));
    this.barToggle?.addEventListener('click', this.handleBarToggle);
    this.barBuild?.addEventListener('click', this.handleBarBuild);
    this.sgOpenButton?.addEventListener('click', this.openSizeGuide);
    this.sgCloseButtons.forEach((button) => button.addEventListener('click', this.closeSizeGuide));
    this.sgTabButtons.forEach((button) => button.addEventListener('click', this.handleSizeGuideTab));
    this.sgTabButtons.forEach((button) => button.addEventListener('keydown', this.handleSizeGuideTabKeys));

    this.setupBarVisibility();
    this.render();
  }

  disconnectedCallback() {
    this.tabButtons?.forEach((button) => button.removeEventListener('click', this.handleTab));
    this.checkoutButtons?.forEach((button) => button.removeEventListener('click', this.handleCheckout));
    this.barToggle?.removeEventListener('click', this.handleBarToggle);
    this.barBuild?.removeEventListener('click', this.handleBarBuild);
    this.sgOpenButton?.removeEventListener('click', this.openSizeGuide);
    this.sgCloseButtons?.forEach((button) => button.removeEventListener('click', this.closeSizeGuide));
    this.sgTabButtons?.forEach((button) => button.removeEventListener('click', this.handleSizeGuideTab));
    this.sgTabButtons?.forEach((button) => button.removeEventListener('keydown', this.handleSizeGuideTabKeys));
    document.removeEventListener('keydown', this.handleSizeGuideKeydown);
    this.heroObserver?.disconnect();
    this.builderObserver?.disconnect();
    this.setScrollLock(false);
  }

  /* ── Empty-state bar visibility ──
     The "Build Your Family Bundle" bar only shows while the builder section
     itself is off-screen. On layouts with a hero above the builder, it also
     waits until the hero has been scrolled past; without a hero (builder at
     the top of the page) the builder's own visibility is the only gate. */

  setupBarVisibility() {
    const hero = document.querySelector('[data-bb-hero], .lp-fb-hero');
    const heroSection = hero?.closest('.shopify-section') || hero;

    this.heroPassed = !heroSection;
    this.builderInView = false;

    if (heroSection) {
      this.heroObserver = new IntersectionObserver(([entry]) => {
        this.heroPassed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        this.updateBarState();
      });
      this.heroObserver.observe(heroSection);
    }

    this.builderObserver = new IntersectionObserver(([entry]) => {
      this.builderInView = entry.isIntersecting;
      this.updateBarState();
    });
    this.builderObserver.observe(this);
  }

  updateBarState() {
    this.dataset.bbBarState = this.heroPassed && !this.builderInView ? 'visible' : 'hidden';
  }

  /* ── Cards ── */

  initCard(el) {
    const jsonEl = el.querySelector('[data-bb-json]');
    if (!jsonEl) return null;

    let data;
    try {
      data = JSON.parse(jsonEl.textContent);
    } catch (error) {
      return null;
    }

    const card = {
      el,
      data,
      selects: Array.from(el.querySelectorAll('[data-bb-option]')),
      addButton: el.querySelector('[data-bb-add]'),
      addLabel: el.querySelector('[data-bb-add-label]'),
      imageEl: el.querySelector('[data-bb-image]'),
      priceEl: el.querySelector('[data-bb-price]'),
      compareEl: el.querySelector('[data-bb-compare]'),
      variant: null,
    };
    card.defaultAddLabel = card.addLabel ? card.addLabel.textContent : '';
    card.staticImage = card.imageEl?.hasAttribute('data-static-image') ? card.imageEl.src : null;

    card.selects.forEach((select) => select.addEventListener('change', () => this.handleOptionChange(card)));
    card.addButton?.addEventListener('click', () => this.addItem(card));

    card.variant = this.resolveVariant(card);
    this.updateCardVariant(card);
    return card;
  }

  resolveVariant(card) {
    if (!card.selects.length) return card.data.variants[0] || null;
    const selected = card.selects
      .slice()
      .sort((a, b) => a.dataset.optionPosition - b.dataset.optionPosition)
      .map((select) => select.value);
    return card.data.variants.find((variant) => variant.options.every((value, index) => value === selected[index])) || null;
  }

  handleOptionChange(card) {
    card.variant = this.resolveVariant(card);
    this.updateCardVariant(card);
    this.updateSwatch(card);
  }

  updateCardVariant(card) {
    const variant = card.variant;
    const soldOutLabel = window.variantStrings?.soldOut || 'Sold out';
    const unavailableLabel = window.variantStrings?.unavailable || 'Unavailable';

    if (card.priceEl && variant) card.priceEl.textContent = this.formatMoney(variant.price);
    if (card.compareEl) {
      const showCompare = variant && variant.compare > variant.price;
      card.compareEl.classList.toggle('hidden', !showCompare);
      if (showCompare) card.compareEl.textContent = this.formatMoney(variant.compare);
    }
    if (card.imageEl && variant?.image && !card.imageEl.hasAttribute('data-static-image')) {
      card.imageEl.src = variant.image;
    }

    if (card.addButton) {
      card.addButton.classList.toggle('is-preorder', Boolean(variant?.available && variant?.preorder));
      if (!variant) {
        card.addButton.disabled = true;
        if (card.addLabel) card.addLabel.textContent = unavailableLabel;
      } else if (!variant.available) {
        card.addButton.disabled = true;
        if (card.addLabel) card.addLabel.textContent = soldOutLabel;
      } else if (variant.preorder) {
        card.addButton.disabled = false;
        if (card.addLabel) card.addLabel.textContent = this.dataset.textPreorder || 'Pre-Order';
      } else {
        card.addButton.disabled = false;
        if (card.addLabel) card.addLabel.textContent = card.defaultAddLabel;
      }
    }
  }

  updateSwatch(card) {
    card.el.querySelectorAll('[data-bb-color-select]').forEach((select) => {
      const dot = select.closest('.lp-fb-bb__select-wrap')?.querySelector('[data-bb-swatch]');
      const url = select.selectedOptions[0]?.dataset.swatch;
      if (dot && url) dot.style.backgroundImage = `url(${url})`;
    });
  }

  cardColor(card) {
    const select = card.el.querySelector('[data-bb-color-select]');
    if (!select) return null;
    return { value: select.value, swatch: select.selectedOptions[0]?.dataset.swatch || '' };
  }

  addItem(card) {
    if (!card.variant || !card.variant.available) return;
    // Each click adds an independent unit, snapshotting the current selection
    this.uidSeq += 1;
    this.items.push({ uid: this.uidSeq, card, variant: card.variant, color: this.cardColor(card) });
    this.flashAdded(card);
    this.render();
  }

  flashAdded(card) {
    if (!card.addButton || card.addButton.disabled) return;
    card.addButton.disabled = true;
    if (card.addLabel) card.addLabel.textContent = this.dataset.textAdded || 'Added ✓';
    setTimeout(() => {
      card.addButton.disabled = false;
      this.updateCardVariant(card);
    }, 800);
  }

  removeItem(uid) {
    this.items = this.items.filter((item) => item.uid !== uid);
    this.render();
  }

  /* ── Tabs ── */

  handleTab = (event) => {
    const tab = event.currentTarget.dataset.bbTab;
    this.tabButtons.forEach((button) => {
      const active = button.dataset.bbTab === tab;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
    this.cards.forEach((card) => {
      card.el.classList.toggle('hidden', tab !== 'all' && card.el.dataset.tab !== tab);
    });
  };

  /* ── Size guide ── */

  get activeTab() {
    const active = this.tabButtons.find((button) => button.classList.contains('is-active'));
    return active?.dataset.bbTab || 'all';
  }

  openSizeGuide = () => {
    if (!this.sg) return;

    // Open on the chart matching the active product filter; "all" falls back to the first tab.
    const filter = this.activeTab;
    const match = this.sgTabButtons.find((button) => button.dataset.bbSgTab === filter);
    this.setSizeGuideTab(match ? filter : this.sgTabButtons[0]?.dataset.bbSgTab);

    this.sg.hidden = false;
    this.setScrollLock(true);
    document.addEventListener('keydown', this.handleSizeGuideKeydown);
    this.sgDialog?.focus();
  };

  closeSizeGuide = () => {
    if (!this.sg || this.sg.hidden) return;

    this.sg.hidden = true;
    this.setScrollLock(false);
    document.removeEventListener('keydown', this.handleSizeGuideKeydown);
    this.sgOpenButton?.focus();
  };

  setScrollLock(locked) {
    document.documentElement.classList.toggle('lp-fb-bb-sg-open', locked);
    document.body.classList.toggle('lp-fb-bb-sg-open', locked);
  }

  setSizeGuideTab(tab) {
    if (!tab) return;

    this.sgTabButtons.forEach((button) => {
      const selected = button.dataset.bbSgTab === tab;
      button.setAttribute('aria-selected', String(selected));
      button.tabIndex = selected ? 0 : -1;
    });
    this.sgPanels.forEach((panel) => {
      panel.hidden = panel.dataset.bbSgPanel !== tab;
    });
  }

  handleSizeGuideTab = (event) => {
    this.setSizeGuideTab(event.currentTarget.dataset.bbSgTab);
  };

  handleSizeGuideTabKeys = (event) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;

    event.preventDefault();
    const step = event.key === 'ArrowRight' ? 1 : -1;
    const index = this.sgTabButtons.indexOf(event.currentTarget);
    const next = this.sgTabButtons[(index + step + this.sgTabButtons.length) % this.sgTabButtons.length];
    this.setSizeGuideTab(next.dataset.bbSgTab);
    next.focus();
  };

  handleSizeGuideKeydown = (event) => {
    if (event.key === 'Escape') {
      this.closeSizeGuide();
      return;
    }

    if (event.key !== 'Tab' || !this.sgDialog) return;

    const focusables = Array.from(
      this.sgDialog.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
    ).filter((el) => !el.closest('[hidden]'));
    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || active === this.sgDialog)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /* ── BOGO math ── */

  computeTotals() {
    const units = [];
    let subtotal = 0;
    let paid = 0;
    let count = 0;

    this.items.forEach((item) => {
      count += 1;
      // Savings baseline is compare_at when present so the panel matches the
      // Rebuy cart pill, which measures against compare_at prices
      subtotal += Math.max(item.variant.compare || 0, item.variant.price);
      paid += item.variant.price;
      units.push(item.variant.price);
    });

    // Shopify's sitewide BOGO rule halves the cheapest eligible unit in each
    // pair, so the preview must discount the floor(n/2) cheapest units to
    // match the discount the cart actually applies
    units.sort((a, b) => a - b);
    const discountedCount = Math.floor(units.length / 2);
    let bogo = 0;
    for (let i = 0; i < discountedCount; i += 1) bogo += Math.floor(units[i] / 2);

    const total = paid - bogo;
    return { count, subtotal, savings: subtotal - total, total };
  }

  /* ── Render ── */

  render() {
    const totals = this.computeTotals();
    const hasItems = totals.count > 0;

    this.cards.forEach((card) => {
      const inBundle = this.items.some((item) => item.card === card);
      card.el.classList.toggle('is-in-bundle', inBundle);
    });

    this.queryAll('[data-bb-count]').forEach((el) => (el.textContent = totals.count));
    this.queryAll('[data-bb-count-items]').forEach((el) => {
      el.textContent = totals.count === 1 ? '1 item' : `${totals.count} items`;
    });
    this.queryAll('[data-bb-savings]').forEach((el) => (el.textContent = this.formatMoney(totals.savings)));
    this.queryAll('[data-bb-subtotal]').forEach((el) => (el.textContent = this.formatMoney(totals.subtotal)));
    this.queryAll('[data-bb-total]').forEach((el) => (el.textContent = this.formatMoney(totals.total)));

    // Savings only appear from the second item on (CRO requirement) and when
    // there is an actual price comparison to show; otherwise hide the savings
    // line and the strikethrough subtotal
    this.dataset.bbSavings = totals.count >= 2 && totals.savings > 0 ? 'yes' : 'no';

    const hasPreorder = this.items.some((item) => item.variant.preorder);
    this.preorderNotes.forEach((el) => (el.hidden = !hasPreorder));

    this.totalsBlocks.forEach((el) => (el.hidden = !hasItems));
    this.checkoutButtons.forEach((button) => (button.disabled = !hasItems));
    this.emptyEls.forEach((el) => (el.hidden = hasItems));
    // The mobile bar stays visible when empty, switching to the "build your bundle" CTA state
    this.dataset.bbCount = totals.count === 0 ? 'none' : totals.count === 1 ? 'one' : 'many';
    if (this.barPill) {
      this.barPill.textContent = totals.count === 1 ? '1 item' : `${totals.count} items selected`;
    }
    if (!hasItems) this.collapseBar();

    this.renderShipping(totals.total);
    this.renderItems();
  }

  renderShipping(total) {
    const fill = this.querySelector('[data-bb-ship-fill]');
    const label = this.querySelector('[data-bb-ship-label]');
    const progress = this.threshold > 0 ? Math.min(1, total / this.threshold) : 1;
    if (fill) fill.style.width = `${Math.round(progress * 100)}%`;
    if (label) {
      if (total <= 0) {
        label.innerHTML = '&nbsp;';
      } else if (progress >= 1) {
        label.textContent = this.dataset.textReached;
      } else {
        label.textContent = this.dataset.textAway.replace('[amount]', this.formatMoney(this.threshold - total));
      }
    }
  }

  renderItems() {
    this.itemsContainers.forEach((container) => {
      container.innerHTML = '';
      this.items.forEach((item) => {
        const node = this.itemTemplate.content.firstElementChild.cloneNode(true);
        const image = node.querySelector('[data-bb-item-image]');
        const imageSrc = item.card.staticImage || item.variant.image;
        if (imageSrc) {
          image.src = imageSrc;
          image.alt = '';
        } else {
          image.remove();
        }
        node.querySelector('[data-bb-item-title]').textContent = item.card.data.title;
        const preorderBadge = node.querySelector('[data-bb-item-preorder]');
        if (preorderBadge) preorderBadge.hidden = !item.variant.preorder;
        const color = item.color;
        const metaOptions = color
          ? item.variant.options.filter((value, index) => index !== item.variant.options.indexOf(color.value))
          : item.variant.options;
        node.querySelector('[data-bb-item-meta]').textContent = metaOptions.join(' · ');
        const colorRow = node.querySelector('[data-bb-item-color]');
        if (color && item.variant.options.includes(color.value)) {
          colorRow.hidden = false;
          colorRow.querySelector('[data-bb-item-color-name]').textContent = color.value;
          if (color.swatch) colorRow.querySelector('[data-bb-item-swatch]').style.backgroundImage = `url(${color.swatch})`;
        }
        const removeButton = node.querySelector('[data-bb-remove]');
        removeButton.setAttribute('aria-label', `Remove ${item.card.data.title} from bundle`);
        removeButton.addEventListener('click', () => this.removeItem(item.uid));
        container.appendChild(node);
      });
    });
  }

  queryAll(selector) {
    return Array.from(this.querySelectorAll(selector));
  }

  /* ── Mobile bar ── */

  handleBarBuild = () => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.querySelector('.lp-fb-bb__grid')?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  };

  handleBarToggle = () => {
    const expanded = this.dataset.expanded === 'true';
    this.dataset.expanded = String(!expanded);
    this.barToggle.setAttribute('aria-expanded', String(!expanded));
  };

  collapseBar() {
    this.dataset.expanded = 'false';
    this.barToggle?.setAttribute('aria-expanded', 'false');
  }

  /* ── Add bundle to cart ── */

  handleCheckout = async (event) => {
    if (!this.items.length) return;
    const button = event.currentTarget;
    button.disabled = true;
    button.classList.add('is-loading');

    // Units are independent in the UI; identical variants merge into one cart line
    const quantities = new Map();
    const preorderIds = new Set();
    this.items.forEach((item) => {
      quantities.set(item.variant.id, (quantities.get(item.variant.id) || 0) + 1);
      if (item.variant.preorder) preorderIds.add(item.variant.id);
    });
    const items = Array.from(quantities, ([id, quantity]) => {
      const line = { id, quantity };
      // Pre-order lines carry the same line-item property Timesact adds on the
      // PDP so fulfillment/CS can identify them in the order
      if (preorderIds.has(id)) {
        line.properties = {
          [this.dataset.preorderPropertyKey || 'Pre-order item']: this.dataset.preorderPropertyValue || 'Delivers as soon as available',
        };
      }
      return line;
    });

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) throw new Error(`Cart add failed: ${response.status}`);

      this.collapseBar();
      if (window.Rebuy?.SmartCart?.show) {
        // Rebuy Smart Cart auto-opens on cart add; make sure it is visible
        window.Rebuy.SmartCart.show();
        if (typeof window.cartCount === 'function') window.cartCount();
      } else if (typeof window.addToCartOk === 'function') {
        window.addToCartOk();
      } else {
        window.location.href = '/cart';
      }
    } catch (error) {
      if (typeof window.addToCartFail === 'function') window.addToCartFail();
    } finally {
      button.disabled = false;
      button.classList.remove('is-loading');
    }
  };

  formatMoney(cents) {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

if (!customElements.get('lp-fb-bundle-builder')) {
  customElements.define('lp-fb-bundle-builder', LpFbBundleBuilder);
}


